/**
 * 
 * Class for 9Sliceable Buttons.
 * 
 * Required asset structure:
 *  Each state is a separate instance of a (separate) movieclip.
 *  The instances names correspond to the states 'up', 'over' etc. For selectable buttons also add 'selectedUp' etc.
 *  All movieclips should be in 1 frame. The 9slice code does not work properly with multiple frames
 * 
 *  Each state movieclip should comply to the following:
 *   For 9 slicing, the bottom most displayobject will be considered as background and scaled by 9slicing. This displayObject must be a Bitmap.
 *   9Slice margins are based on the total bounding box of the other child displayobjects, typically the label textfield, as in this movieclip.
 *   The actual metrics of the dummy text are used to determine the bounding box of the textfield, not the dashed outline of the textfield! 
 *   So make sure the dummy text in the textfield fills the dashed bounds of the textfield as much as possible.
 *   If a label textfield is present, it should have the instance name 'textfield' for every state.
 * 
 * Originally used in the '905-ING Culturefit' project. 
 *  See app/src/assets/skins/EndAssets.fla:GhostButton9Sliceable for example
 * 
 * Created on 13-apr-2017, 13:49:34
 * @author Reinier Feijen for &ranj
 **/

import Button from "../buttons/Button"
import Slice9Panel from "../scale9/Slice9Panel"

export default class Slice9Button extends Button {
    /**
     * @constructor
     * @param {*} skin The button's skin
     * @param {*} mode Optional. Type of 9slicing: Slice9Panel.HORIZONTAL, Slice9Panel.VERTICAL, Slice9Panel.BOTH (default)
     * @param {*} options Optional. Options object with cacheBitmap, pixelPerfect, overlap, and wordWrap properties
     */
    constructor(skin, mode = Slice9Button.HORIZONTAL, options = { cacheBitmap: true, pixelPerfect: false, overlap: false, wordWrap: false }) {
        //hacky way to have constructor arguments available before call to super()
        skin.options = {
            slice9mode: mode,
            cacheBitmap: options.cacheBitmap,
            pixelPerfect: options.pixelPerfect,
            overlap: options.overlap,
        };

        super(skin, options.wordWrap !== undefined ? options.wordWrap : false);
    }

    setLabel(string) {
        this._label = string || "";
        //Set label string in each state movieclip that has a Text instance named textfield
        var states = this._states;
        for (var i = 0; i < states.length; i++) {
            var state = states[i];
            if (this.skin[state]) {
                var instance = this.skin[state];
                var tf = instance.textfield;
                if (tf) {
                    tf.text = this._label;
                    if (this._wordWrap) {
                        this._applyWordWrap(tf);
                    }
                }
                instance.sliced.update();
            }
        }
        this._setState(this._state);
    }

    /**
     * Retrieves the bounds of the scaled button in its current state. 
     * Based on the bounds calculation of the underlying Slice9Panel(s).
     * 
     * @returns bounds of the scaled button in its current state.
     */
    getBounds() {
        return this.skin[this._state].sliced.getBounds();
    }

    setMinWidth(width) {
        for (const state of this._states) {
            this.skin[state].sliced.setMinWidth(width);
        }
    }

    setMinHeight(height) {
        for (const state of this._states) {
            this.skin[state].sliced.setMinHeight(height);
        }
    }

    _setState(state) {
        var stateIn = state;
        this._initialize9Slicing();
        this._state = state;
        //9slice buttons do not have separate frames, but all states as movieclip instances in 1 frame
        if (this._selected) {
            var stateSelected = "selected" + state.substr(0, 1).toUpperCase() + state.substr(1);
            if (this.skin[stateSelected] != null) state = stateSelected;
        }
        //If state does not exist, fall back to 'up'
        var attempt = 0;
        while (!this.skin[state] && attempt < 7) {
            state = this._statesFallback[state];
            attempt++;
        }
        if (!state) {
            console.warn("Slice9Button._setState, no instance (or fallback instance) found for state: " + stateIn);
            return;
        }
        //
        var states = this._states;
        for (var i = 0; i < states.length; i++) {
            var stateTemp = states[i];
            var instance = this.skin[stateTemp];
            instance.visible = state == stateTemp;
        }
        this._changed();
    }

    _initialize9Slicing() {
        if (this._initialized9Slicing) return;
        this._initialized9Slicing = true;

        const STATES = [Button.UP, Button.OVER, Button.DOWN, Button.DISABLED];
        const STATES_FALLBACK = {};

        //Pointer to constants can be overwritten for individual buttons
        this._statesFallback = STATES_FALLBACK;
        this._states = STATES;

        STATES_FALLBACK[Button.UP] = null;//has no fallback
        STATES_FALLBACK[Button.DOWN] = Button.UP;
        STATES_FALLBACK[Button.OVER] = Button.DOWN;
        STATES_FALLBACK[Button.DISABLED] = Button.DOWN;

        var states = this._states;
        this.skin.gotoAndStop(0);//workaround: triggers populating of children property
        for (var i = 0; i < states.length; i++) {
            var state = states[i];
            if (this.skin[state]) {
                var instance = this.skin[state];
                instance.gotoAndStop(0);//workaround: triggers populating of children property
                const bitmap = instance.children.find(child => child instanceof createjs.Bitmap);
                const mode = this.skin.options.slice9mode ?? Slice9Panel.VERTICAL;
                const cacheBitmap = this.skin.options.cacheBitmap ?? true;
                const pixelPerfect = this.skin.options.pixelPerfect ?? false;
                const overlap = this.skin.options.overlap ?? false;
                instance.sliced = new Slice9Panel(instance, bitmap, { mode, cacheBitmap, pixelPerfect, overlap, });

                if (mode == Slice9Button.HORIZONTAL) {
                    var tf = instance.textfield;
                    if (tf)
                        tf.lineWidth = null;//This will avoid wordwrap when text is larger than lineWidth as defined in asset.
                }
            }
        }
    }
}

Slice9Button.HORIZONTAL = Slice9Panel.HORIZONTAL;
Slice9Button.VERTICAL = Slice9Panel.VERTICAL;
